import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { X, FileSignature, Send, Pencil, RotateCw, AlertTriangle, Loader2, Info } from "lucide-react";

// Iter 109 — Streamer "Your contracts" — SINGLE section (Iter 68 legacy
// StreamerContracts.jsx removed from the dashboard). Compact list with a
// grouped table (state buckets shown as row separators), click-through
// opens `ContractDetailModal` with the full details + state-appropriate
// actions (Edit / Send / Resend). Iter 107 endpoints, credit-move logic,
// bounds, commission, settlement, PII masking all untouched.

const CATEGORY_LABELS = {
  slots: "Slots", sports: "Sports", live_casino: "Live Casino",
  table_games: "Table Games", house_originals: "House / Originals", dice: "Dice",
};
const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—");
const maskPlayer = () => "P" + "******";   // symmetric mask (Iter 97/104 pattern)

const STATE_BUCKETS = [
  { key: "draft",                          label: "Draft (not sent)",             tint: "border-white/25 text-[#8B97AE]" },
  { key: "pending_player_confirmation",    label: "Pending (awaiting player)",    tint: "border-[#F5B841]/50 text-[#F5B841]" },
  { key: "change_requested",               label: "Change requested",             tint: "border-[#F26D6D]/60 text-[#F26D6D]" },
  { key: "active",                         label: "Active",                       tint: "border-emerald-500/50 text-emerald-400" },
  { key: "finished",                       label: "Finished",                     tint: "border-white/40 text-white" },
];
const FINISHED_ALIASES = new Set(["finished", "settled", "expired", "declined", "cancelled"]);
const bucketOf = (s) => {
  if (s === "draft") return "draft";
  if (s === "pending_player_confirmation" || s === "pending_payment") return "pending_player_confirmation";
  if (s === "change_requested") return "change_requested";
  if (s === "active") return "active";
  if (FINISHED_ALIASES.has(s)) return "finished";
  return null;
};

function StatePill({ status }) {
  const meta = STATE_BUCKETS.find((b) => b.key === bucketOf(status));
  const tint = meta?.tint || "border-white/20 text-[#8B97AE]";
  return <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${tint}`}>{status.replace(/_/g, " ")}</span>;
}

async function loadCategoryConfig(vertical) {
  try {
    const { data } = await api.get(`/config/contract-categories/${encodeURIComponent(vertical)}`);
    return data;
  } catch { return { configured: false }; }
}

// ---------------------------------------------------------------------------
// EditContractModal — reused from Iter 108. Live re-quotes via the shared
// `/chats/{cid}/contract-formation` endpoint. In change_requested, shows
// Current/New/Delta/Available and blocks when available < delta.
// ---------------------------------------------------------------------------
function EditContractModal({ contract, onClose, onSaved }) {
  const isDraft = contract.status === "draft";
  const isChangeRequested = contract.status === "change_requested";
  // Iter 121 — Track where a mousedown started. Only a gesture whose ENTIRE
  // press-and-release stays on the backdrop counts as a "backdrop click".
  // Prevents text-selection drags / spinner mis-clicks / drift-releases from
  // firing onClose (Iter 119's onClick guard alone was insufficient because
  // React's synthetic click target is the common ancestor of down + up).
  const backdropPressRef = useRef(false);
  const onBackdropMouseDown = (e) => { backdropPressRef.current = e.target === e.currentTarget; };
  const onBackdropClick = (e) => {
    const started = backdropPressRef.current;
    backdropPressRef.current = false;
    if (started && e.target === e.currentTarget) onClose();
  };
  const [config, setConfig] = useState(null);
  const [cats, setCats] = useState(() => {
    const c = contract.wagerCategories || {};
    const out = {};
    Object.keys(c).forEach((k) => { out[k] = String(c[k] ?? ""); });
    return out;
  });
  const [casino, setCasino] = useState(contract.casino || "");
  const [affCode, setAffCode] = useState(contract.affiliateCode || "");
  const [refLink, setRefLink] = useState(contract.referralLink || "");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [blockedErr, setBlockedErr] = useState(null);

  useEffect(() => { loadCategoryConfig(contract.vertical || "casino").then(setConfig); }, [contract.vertical]);

  useEffect(() => {
    if (!config?.configured || !contract.chatId) return;
    const numeric = {};
    let anyPositive = false;
    (config.categories || []).forEach((c) => {
      const n = Number(cats[c.key] || 0);
      const clean = Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
      numeric[c.key] = clean;
      if (clean > 0) anyPositive = true;
    });
    if (!anyPositive) { setPreview(null); return; }
    let cancelled = false;
    setPreviewing(true);
    api.get(`/chats/${contract.chatId}/contract-formation`, { params: { wagerCategoriesJson: JSON.stringify(numeric) } })
      .then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch(() => { if (!cancelled) setPreview(null); })
      .finally(() => { if (!cancelled) setPreviewing(false); });
    return () => { cancelled = true; };
  }, [cats, config, contract.chatId]);

  const localWeighted = useMemo(() => {
    if (!config?.configured) return 0;
    let sum = 0;
    (config.categories || []).forEach((c) => {
      const amt = Number(cats[c.key] || 0);
      if (Number.isFinite(amt) && amt > 0) sum += amt * Number(c.coefficient || 0);
    });
    return Math.round(sum * 100) / 100;
  }, [cats, config]);

  const currentReserved = Number(contract.reservedCreditsAmount || 0);
  const X_new = preview?.reserveAmount ?? null;
  const delta = X_new == null ? null : Math.round((X_new - currentReserved) * 100) / 100;
  const inBounds = preview?.inBounds ?? false;
  const affValid = casino.trim().length > 0 && affCode.trim().length > 0;
  const linkValid = !refLink.trim() || /^https?:\/\//i.test(refLink.trim());
  const additionalNeeded = isChangeRequested && delta != null && delta > 0 ? delta : 0;
  const availableOk = additionalNeeded === 0 || (preview?.availableCredits ?? Infinity) >= additionalNeeded;
  // Iter 120 — Structural gates (button disabled + reasons the streamer can
  // read at a glance). We NO LONGER disable Save on bounds/credits — those
  // now surface a specific toast/inline block on click, matching the
  // Iter 107 server-side error shape.
  const structuralBlocked = !affValid || !linkValid || saving;
  const blockReason = (() => {
    if (!affValid) return "Casino operator + affiliate code are required.";
    if (!linkValid) return "Referral link must start with http:// or https://";
    if (previewing || !preview) return "Waiting for reserve preview — try again in a moment.";
    if (!inBounds) return `Weighted wager is outside the 70%–200% band (${fmt(preview.minAgreed)} – ${fmt(preview.maxAgreed)}). Adjust categories to bring the total inside.`;
    if (isChangeRequested && !availableOk) return `Not enough credits for the +${fmt(additionalNeeded)} additional reserve. Top up to continue.`;
    return null;
  })();
  const canSave = !structuralBlocked && !blockReason;

  const save = async () => {
    if (structuralBlocked) return;
    // Iter 120 — Never fail silently. If any soft-block reason is active,
    // surface it as a toast so the streamer knows why Save isn't advancing.
    if (blockReason) { toast.error(blockReason); return; }
    setBlockedErr(null); setSaving(true);
    try {
      const numeric = {};
      (config.categories || []).forEach((c) => {
        const n = Number(cats[c.key] || 0);
        numeric[c.key] = Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
      });
      const { data } = await api.patch(`/contracts/${contract.id}`, {
        wagerCategories: numeric, casino: casino.trim(),
        affiliateCode: affCode.trim(), referralLink: refLink.trim() || "",
      });
      toast.success(isChangeRequested ? "Edits saved — hit Resend to send back to the player" : "Draft updated");
      onSaved?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "insufficient_credits") {
        setBlockedErr({ required: d.required, available: d.available });
      } else {
        toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="edit-contract-modal" onMouseDown={onBackdropMouseDown} onClick={onBackdropClick}>
      <div className="w-full max-w-xl bg-[#0B1A14] border border-[#2DD4A7]/40 shadow-2xl max-h-[90vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#0B1A14]">
          <div className="flex items-center gap-2"><Pencil className="w-4 h-4 text-[#2DD4A7]" /><h3 className="font-display font-bold text-base text-[#E8ECF4]">Edit contract</h3></div>
          <button data-testid="edit-close" onClick={onClose} className="text-[#8B97AE] hover:text-[#E8ECF4]"><X className="w-4 h-4" /></button>
        </div>
        {!config ? (
          <div className="p-8 flex items-center justify-center gap-2 text-xs text-[#8B97AE]"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : !config.configured ? (
          <div className="p-6 text-xs text-[#8B97AE]">Not configured for this vertical.</div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]">Where to register</div>
            <div className="space-y-2">
              <label className="block"><div className="text-[11px] text-[#E8ECF4] mb-0.5">Casino / operator <span className="text-[#F87171]">*</span></div>
                <input data-testid="edit-casino" value={casino} onChange={(e) => setCasino(e.target.value)} className="w-full bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-2 font-mono outline-none focus:border-[#2DD4A7]" /></label>
              <label className="block"><div className="text-[11px] text-[#E8ECF4] mb-0.5">Affiliate code <span className="text-[#F87171]">*</span></div>
                <input data-testid="edit-aff" value={affCode} onChange={(e) => setAffCode(e.target.value)} maxLength={64} className="w-full bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-2 font-mono outline-none focus:border-[#2DD4A7]" /></label>
              <label className="block"><div className="text-[11px] text-[#E8ECF4] mb-0.5">Referral link <span className="text-[#8B97AE]">(optional)</span></div>
                <input data-testid="edit-link" value={refLink} onChange={(e) => setRefLink(e.target.value)} maxLength={500} placeholder="https://…" className="w-full bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-2 font-mono outline-none focus:border-[#2DD4A7]" />
                {!linkValid && <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">Must start with http:// or https://</div>}</label>
            </div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]">Agreed monthly wager per category · 90-day term</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(config.categories || []).map((c) => (
                <label key={c.key} className="block">
                  <div className="flex items-baseline justify-between mb-0.5"><span className="text-[11px] text-[#E8ECF4]">{CATEGORY_LABELS[c.key] || c.key}</span><span className="text-[9px] font-mono text-[#8B97AE]">×{Math.round((c.coefficient || 0) * 100)}%</span></div>
                  <input data-testid={`edit-cat-${c.key}`} type="number" inputMode="decimal" min={0} step="1"
                    value={cats[c.key] ?? ""} onChange={(e) => setCats((prev) => ({ ...prev, [c.key]: e.target.value }))}
                    placeholder="0" className="w-full bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-2 font-mono outline-none focus:border-[#2DD4A7]" />
                </label>
              ))}
            </div>
            <div className="border border-white/10 p-3 space-y-1.5">
              <div className="flex justify-between text-sm"><span className="text-[#8B97AE]">Agreed weighted wager</span><span className="font-mono text-[#E8ECF4]" data-testid="edit-weighted">{fmt(preview?.agreedWeightedWager ?? localWeighted)}</span></div>
              {preview && (<>
                <div className="flex justify-between text-xs"><span className="text-[#8B97AE]">Bounds (70%–200%)</span><span className="font-mono text-[#E8ECF4]">{fmt(preview.minAgreed)} – {fmt(preview.maxAgreed)}</span></div>
                {isChangeRequested && (<>
                  <div className="flex justify-between text-xs"><span className="text-[#8B97AE]">Current reserved</span><span className="font-mono text-[#E8ECF4]">{fmt(currentReserved)} cr</span></div>
                  <div className="flex justify-between text-xs"><span className="text-[#8B97AE]">New reserved (after save)</span><span className="font-mono text-[#2DD4A7]" data-testid="edit-new-reserved">{fmt(X_new)} cr</span></div>
                  <div className="flex justify-between text-xs"><span className="text-[#8B97AE]">Delta</span><span className={`font-mono ${delta > 0 ? "text-[#F5B841]" : delta < 0 ? "text-[#2DD4A7]" : "text-[#8B97AE]"}`} data-testid="edit-delta">
                    {delta == null ? "—" : (delta > 0 ? `+${fmt(delta)} to bind` : delta < 0 ? `${fmt(delta)} back to available` : "no change")}</span></div>
                  <div className="flex justify-between text-xs pt-1 border-t border-white/10 mt-1.5"><span className="text-[#8B97AE]">Available</span><span className="font-mono text-[#E8ECF4]">{fmt(preview.availableCredits)} cr</span></div>
                </>)}
              </>)}
              {previewing && <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Re-quoting…</div>}
            </div>
            {isChangeRequested && additionalNeeded > 0 && !availableOk && (
              <div className="flex items-center gap-2 text-xs text-[#F87171] border border-[#F87171]/40 bg-[#F87171]/[0.06] p-2" data-testid="edit-blocked">
                <AlertTriangle className="w-4 h-4 shrink-0" /><span>Not enough credits for the additional {fmt(additionalNeeded)} required. Top up to continue.</span></div>
            )}
            {blockedErr && (
              <div className="flex items-center gap-2 text-xs text-[#F87171] border border-[#F87171]/40 bg-[#F87171]/[0.06] p-2" data-testid="edit-server-blocked">
                <AlertTriangle className="w-4 h-4 shrink-0" /><span>Blocked: need {fmt(blockedErr.required)} cr, have {fmt(blockedErr.available)} cr.</span></div>
            )}
            {blockReason && !structuralBlocked && (
              <div className="flex items-center gap-2 text-xs text-[#F5B841] border border-[#F5B841]/40 bg-[#F5B841]/[0.06] p-2" data-testid="edit-block-reason">
                <AlertTriangle className="w-4 h-4 shrink-0" /><span>{blockReason}</span>
              </div>
            )}
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Reserved credits are bound to the live contract, not spent. They only move to consumed on settlement.</p>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/10 sticky bottom-0 bg-[#0B1A14] py-2">
              <button onClick={onClose} className="px-4 h-9 border border-white/20 text-xs font-mono uppercase tracking-widest text-[#8B97AE] hover:text-[#E8ECF4] hover:bg-white/5">Cancel</button>
              <button data-testid="edit-save" onClick={save} disabled={structuralBlocked}
                title={blockReason || undefined}
                className={`px-4 h-9 text-xs font-mono uppercase tracking-widest transition-colors ${canSave ? "bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841]" : "bg-[#2DD4A7]/40 text-[#04150F]/70 hover:bg-[#F5B841]/40"} disabled:opacity-40`}>{saving ? "…" : "Save"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ContractDetailModal — rich view + state-appropriate actions.
// ---------------------------------------------------------------------------
function Field({ label, children, testId }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-[#8B97AE] mb-0.5">{label}</div>
      <div className="text-xs font-mono text-[#E8ECF4]" data-testid={testId}>{children}</div>
    </div>
  );
}

function ContractDetailModal({ contract, onClose, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  // Iter 121 — same press-and-release guard as EditContractModal so a drag
  // from inside → out (e.g. selecting text in a field) can't close the modal.
  const backdropPressRef = useRef(false);
  const onBackdropMouseDown = (e) => { backdropPressRef.current = e.target === e.currentTarget; };
  const onBackdropClick = (e) => {
    const started = backdropPressRef.current;
    backdropPressRef.current = false;
    if (started && e.target === e.currentTarget) onClose();
  };
  const c = contract;
  const isDraft = c.status === "draft";
  const isChangeRequested = c.status === "change_requested";
  const rounds = Number(c.changeRoundCount || 0);
  const lastNote = Array.isArray(c.changeRequests) && c.changeRequests.length ? c.changeRequests[c.changeRequests.length - 1] : null;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${c.id}/send`);
      const reserved = Number(data?.contract?.reservedCreditsAmount || 0);
      toast.success(`Sent — ${fmt(reserved)} credits reserved`);
      await onRefresh?.();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "insufficient_credits") {
        toast.error(`Not enough credits — need ${fmt(d.required)}, have ${fmt(d.available)}`);
      } else {
        toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
      }
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/contracts/${c.id}/resend`);
      toast.success("Resent to the player");
      await onRefresh?.();
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  const catEntries = Object.entries(c.wagerCategories || {});
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="contract-detail-modal" onMouseDown={onBackdropMouseDown} onClick={onBackdropClick}>
      <div className="w-full max-w-2xl bg-[#0B1A14] border border-white/20 shadow-2xl max-h-[90vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#0B1A14]">
          <div className="flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-[#2DD4A7]" />
            <h3 className="font-display font-bold text-base text-[#E8ECF4]">Contract detail</h3>
            <StatePill status={c.status} />
          </div>
          <button data-testid="detail-close" onClick={onClose} className="text-[#8B97AE] hover:text-[#E8ECF4]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field label="Player" testId="detail-player">{maskPlayer()}</Field>
            <Field label="Vertical">{c.vertical || "—"}</Field>
            <Field label="Created">{(c.createdAt || "").slice(0, 10)}</Field>
            <Field label="Agreed weighted wager" testId="detail-weighted"><span className="text-[#2DD4A7]">{fmt(c.agreedWeightedWager ?? c.agreed_monthly_volume)}</span></Field>
            <Field label="70% floor"><span className="text-[#F5B841]">{fmt(c.floor_minimum ?? c.floorMinimum)}</span></Field>
            <Field label="Rounds used" testId="detail-rounds">{rounds}/3</Field>
            <Field label="Reserved credits" testId="detail-reserved">{fmt(c.reservedCreditsAmount || 0)} cr</Field>
            <Field label="Chat-unlock paid">{fmt(c.chatUnlockCharged || 0)} cr</Field>
            {c.payment_confirmed_at && <Field label="Sent at">{(c.payment_confirmed_at || "").slice(0, 16).replace("T", " ")}</Field>}
            {c.term_start_at && <Field label="Started">{(c.term_start_at || "").slice(0, 10)}</Field>}
            {c.endedAt && <Field label="Ended">{(c.endedAt || "").slice(0, 10)}</Field>}
            {c.endedReason && <Field label="Ended reason">{c.endedReason.replace(/_/g, " ")}</Field>}
          </div>

          {catEntries.length > 0 && (
            <div className="border border-white/10 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-2">Category breakdown</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs font-mono" data-testid="detail-categories">
                {catEntries.map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/5 py-0.5">
                    <span className="text-[#8B97AE]">{CATEGORY_LABELS[k] || k}</span>
                    <span className="text-[#E8ECF4]">{fmt(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border border-[#F5B841]/30 bg-[#F5B841]/[0.04] p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-2">Where to register</div>
            <div className="text-xs text-[#E8ECF4] space-y-0.5">
              <div>Casino: <span className="font-mono">{c.casino || "—"}</span></div>
              <div>Affiliate code: <span className="font-mono text-[#F5B841]">{c.affiliateCode || "—"}</span></div>
              {c.referralLink && <div>Referral link: <a href={c.referralLink} target="_blank" rel="noopener noreferrer" className="font-mono text-[#2DD4A7] underline break-all">{c.referralLink}</a></div>}
            </div>
          </div>

          {isChangeRequested && lastNote && (
            <div className="border border-[#F26D6D]/40 bg-[#F26D6D]/[0.06] p-3 space-y-1" data-testid="detail-change-note">
              <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#F26D6D]"><Info className="w-3 h-3" />Player requested changes · round {lastNote.round}</div>
              <div className="text-xs text-[#E8ECF4] whitespace-pre-wrap break-words">{lastNote.note}</div>
            </div>
          )}

          {(c.endedReason === "change_cutoff") && (
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="detail-ended-detail">
              Retained {fmt(c.retainedFromReserve || 0)} cr · Refunded {fmt(c.refundedFromReserve || 0)} cr
            </div>
          )}
          {(c.endedReason === "admin_cancelled") && (
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="detail-ended-detail">
              Refunded {fmt(c.refundedFromReserve || 0)} cr · admin cancelled
            </div>
          )}
          {(c.endedReason === "player_cancellation") && (
            <div className="border border-[#F5B841]/40 bg-[#F5B841]/[0.05] p-3" data-testid="detail-ended-cancelled">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-1">Cancelled by player · admin-approved</div>
              <div className="text-xs font-mono text-[#E8ECF4]">
                Retained <span className="text-[#F5B841]">{fmt(c.retainedFromReserve || 0)} cr</span>
                &nbsp;·&nbsp;Released <span className="text-[#2DD4A7]">{fmt(c.refundedFromReserve || 0)} cr</span> back to available
              </div>
              <div className="text-[10px] font-mono text-[#8B97AE] mt-1">Retained equals the chat-unlock you paid up front. Released is now spendable on new contracts.</div>
            </div>
          )}

          {(isDraft || isChangeRequested) && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
              <button data-testid="detail-edit" onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 px-3 h-9 border border-white/20 text-xs font-mono uppercase tracking-widest text-[#E8ECF4] hover:bg-white/5"><Pencil className="w-3 h-3" />Edit</button>
              {isDraft && (
                <button data-testid="detail-send" onClick={send} disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 h-9 bg-[#2DD4A7] text-[#04150F] text-xs font-mono uppercase tracking-widest hover:bg-[#F5B841] transition-colors disabled:opacity-40"><Send className="w-3 h-3" />{busy ? "…" : "Send"}</button>
              )}
              {isChangeRequested && (
                <button data-testid="detail-resend" onClick={resend} disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 h-9 bg-[#2DD4A7] text-[#04150F] text-xs font-mono uppercase tracking-widest hover:bg-[#F5B841] transition-colors disabled:opacity-40"><RotateCw className="w-3 h-3" />{busy ? "…" : "Resend"}</button>
              )}
              <div className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] self-center">
                Reserved credits are bound, not spent.
              </div>
            </div>
          )}
          {c.status === "pending_player_confirmation" && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Waiting on the player to confirm or request changes.</div>
          )}
        </div>
      </div>
      {editing && (
        <EditContractModal
          contract={c}
          onClose={() => setEditing(false)}
          onSaved={async () => { setEditing(false); await onRefresh?.(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Streamer "Your contracts" — compact list; grouped by state; click a row
// to open ContractDetailModal.
// ---------------------------------------------------------------------------
export default function StreamerIter3Contracts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts/mine");
      setRows((data || []).filter((r) => r.flow === "iter3"));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = { draft: [], pending_player_confirmation: [], change_requested: [], active: [], finished: [] };
    rows.forEach((c) => {
      const b = bucketOf(c.status);
      if (b) g[b].push(c);
    });
    return g;
  }, [rows]);

  const total = rows.length;
  if (!loading && total === 0) return null;
  const openContract = openId ? rows.find((r) => r.id === openId) : null;

  return (
    <section className="lg:col-span-12 border border-white/15 p-6 lg:p-8" data-testid="streamer-iter3-contracts">
      <div className="flex items-center gap-2 mb-4">
        <FileSignature className="w-4 h-4 text-[#2DD4A7]" />
        <div className="font-display font-bold text-base text-[#E8ECF4]">Your contracts</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{total} total · click a row for details</div>
      </div>

      <div className="space-y-5">
        {STATE_BUCKETS.map((bucket) => {
          const items = grouped[bucket.key] || [];
          if (items.length === 0) return null;
          return (
            <div key={bucket.key} data-testid={`bucket-${bucket.key}`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">{bucket.label} · {items.length}</div>
              <div className="border border-white/10">
                <table className="w-full text-sm">
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                    <tr className="border-b border-white/10 bg-white/[0.02]">
                      <th className="text-left p-2 w-[22%]">Player</th>
                      <th className="text-left p-2 w-[18%]">State</th>
                      <th className="text-right p-2 w-[22%]">Agreed weighted</th>
                      <th className="text-right p-2 w-[18%]">Reserved</th>
                      <th className="text-left p-2 w-[20%]">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => (
                      <tr
                        key={c.id}
                        data-testid={`contract-row-${c.id}`}
                        onClick={() => setOpenId(c.id)}
                        className="border-b border-white/5 last:border-b-0 cursor-pointer hover:bg-white/[0.03]"
                      >
                        <td className="p-2 font-mono text-xs">{maskPlayer()}</td>
                        <td className="p-2"><StatePill status={c.status} /></td>
                        <td className="p-2 font-mono text-xs text-right text-[#2DD4A7]">{fmt(c.agreedWeightedWager ?? c.agreed_monthly_volume)}</td>
                        <td className="p-2 font-mono text-xs text-right text-[#E8ECF4]">{fmt(c.reservedCreditsAmount || 0)} cr</td>
                        <td className="p-2 font-mono text-xs text-[#8B97AE]">{(c.createdAt || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {openContract && (
        <ContractDetailModal
          contract={openContract}
          onClose={() => setOpenId(null)}
          onRefresh={load}
        />
      )}
    </section>
  );
}
